// src/components/ClaimFormActions.js
//
// SECTION 517 (2026-09-29, overnight; Bob: "can you execute them tonight without me?" - yes, items 1, 2, 4).
// The claimant's own acts on the claim form, on the claim page:
//   - RULING 258: approve (or edit and approve) the Statement of Claim - the draft in the agreement's words where the
//     platform drafted one, otherwise her own words. Until approved the form stays "not ready to send".
//   - RULING 259: a missing mailing address is asked for here, from the person it belongs to, and saved to her profile
//     and to this claim (the claim's contact block is the address the carrier replies to). An officer filing is asked
//     for his own address, saved to his profile. Never guessed, never a dead end.
//   - RULING 260: the claimant may elect not to sign; she is shown the unsigned-claim warning first, and her answer,
//     the time and the exact wording she was shown are recorded on the claim. She may change her mind.
// Each write is ONE field group on the claim, allowed by firestore.rules claimantRecordsOwnChoices() (section 517).
// Record 605 (ruling 138): EXCEPT the Statement of Claim on a FILED claim. That is her fact, and she changes or withdraws
// it as a recorded act through the factsAct Cloud Function (FactsActsPanel.js callFactsAct): the earlier text is kept,
// the act is dated, and her Local and General Chairpersons are told. claimantRecordsOwnChoices() no longer admits it.
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import ClaimLanguageHelper from './ClaimLanguageHelper';   // ruling 265
import { factsFromClaim } from '../data/claimLanguage';
import { claimPatch } from '../data/claimWrite';
import { profilePatch } from '../data/memberProfile';   // PRF-1: every write-back to users/ passes through profilePatch()
import FactsActsPanel, { callFactsAct, factsCaller } from './FactsActsPanel';   // Record 605 (ruling 138)
import { factsClaimant, isWithdrawn } from '../data/factsActs';   // Record 605 (ruling 138)

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C', panel: '#0A1628' };
const s = (v) => (v == null ? '' : String(v).trim());

export const UNSIGNED_WARNING_VERSION = '260-v1';
export const UNSIGNED_WARNING = [
  'You are choosing not to sign your claim.',
  'Your signature is your authority for this claim. Without it:',
  '- The Carrier may argue the claim was not made or authorized by you, and may try to deny it on that ground.',
  '- Your Local or General Chairperson can still submit it, signed as a union officer.',
  '- You may change your mind and sign it before it is submitted.',
  'This notice and your answer are recorded on your claim.',
];

// Is the signed-in person the claimant on this claim? Mirrors the claims read rule's claimant arm.
export function isClaimantOf(claim, user) {
  const c = claim || {}; const u = user || {};
  const uid = u.uid || (auth.currentUser && auth.currentUser.uid) || '';
  const email = s(u.email || (auth.currentUser && auth.currentUser.email)).toLowerCase();
  if (uid && s(c.claimantUid) === uid) return true;
  if (email && s(c.claimantEmail).toLowerCase() === email) return true;
  return false;
}

export default function ClaimFormActions({ claimId, claim, user, model, filingBy, onPatched }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [addr, setAddr] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  if (!claim || !model || !claimId) return null;

  const mine = isClaimantOf(claim, user);
  // RULING 258 said "the claimant (or the officer filing for her)". A claimant with no account on the platform (claim
  // 0013, Mr. Coyle) could not approve at all, so nothing could. The officer who may work the claim approves it for him,
  // and the approval says so.
  const officer = user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair');
  const noAccount = !s(claim.claimantUid) && !s(claim.claimantEmail);
  const mayApprove = mine || (officer && noAccount);
  const uid = (user && user.uid) || (auth.currentUser && auth.currentUser.uid) || '';
  const write = async (patch, label) => {
    setBusy(label); setErr('');
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({ ...patch, updatedAt: serverTimestamp() }, user || auth.currentUser));
      if (onPatched) onPatched(patch);
      return true;
    } catch (e) {
      setErr('Not saved: ' + ((e && (e.code || e.message)) || 'the write was refused') + '. Nothing was changed.');
      return false;
    } finally { setBusy(''); }
  };

  // ---------------------------------------------------------------- RULING 258: the statement
  // Record 605 (ruling 138): a statement she WITHDREW stays on the claim (factsWithdrawn marks it) but is not an approval.
  const socWithdrawn = isWithdrawn(claim, 'statementOfClaim');
  const approved = claim.statementOfClaim && s(claim.statementOfClaim.approvedBy) && !socWithdrawn ? claim.statementOfClaim : null;
  const shown = (model.form.substance[0] && s(model.form.substance[0].text)) || s(claim.description);
  const drafted = model.missing.some(m => /drafted from the claim/.test(m));
  // Record 605 (ruling 138): ON A FILED CLAIM THE CLAIMANT'S STATEMENT CHANGES ONLY AS A RECORDED ACT. The factsAct
  // function writes the act (the earlier text kept), the claim, and the notices to her Local and General Chairpersons;
  // firestore.rules no longer lets her write statementOfClaim directly on a filed claim. A draft is still edited
  // directly (claimantEdit), and an officer approving for a claimant with no account still writes directly (ruling 258).
  const statementByAct = mine && claim.status !== 'draft';
  const factsWho = factsClaimant(claim, factsCaller(user));
  const actBlocked = statementByAct && !factsWho.may ? factsWho.because : '';
  const saveStatement = async (t, edited) => {
    const soc = { text: t, approvedBy: uid, approvedByName: s(user && user.name) + (mine ? '' : ' (for the claimant, who has no account)'), approvedAt: new Date().toISOString(), draftedFrom: drafted ? 'template' : 'her own words', edited };
    if (!statementByAct) return write({ statementOfClaim: soc }, 'approve');
    setBusy('approve'); setErr('');
    try {
      const d = await callFactsAct({ claimId, kind: 'changed', field: 'statementOfClaim', text: t, draftedFrom: soc.draftedFrom, edited });
      if (onPatched && d.view) onPatched(d.view);
      return true;
    } catch (e) {
      setErr('Not recorded: ' + ((e && e.message) || 'the act was refused') + '. Nothing was changed.');
      return false;
    } finally { setBusy(''); }
  };

  // ---------------------------------------------------------------- RULING 259: the address
  const needClaimantAddr = mine && model.missing.some(m => /claimant.s mailing address/.test(m));
  const needOfficerAddr = !mine && filingBy !== 'claimant' && model.missing.some(m => /mailing address of the/.test(m));
  const openAddr = async () => {
    let p = {};
    try { const d = await getDoc(doc(db, 'users', uid)); if (d.exists()) p = d.data(); } catch (e) { /* profile unreadable - start blank */ }
    setAddr({ street: s(p.street) || s(claim.claimantStreet), city: s(p.city) || s(claim.claimantCity), state: s(p.state) || s(claim.claimantState), zip: s(p.zip) || s(claim.claimantZip) });
  };
  const saveAddr = async () => {
    const a = addr || {};
    if (!s(a.street) || !s(a.city) || !s(a.state) || !s(a.zip)) { setErr('Street, city, state and ZIP are all needed.'); return; }
    setBusy('addr'); setErr('');
    try {
      // The profile first: it is the person's own record, used on every claim and letter from now on.
      await setDoc(doc(db, 'users', uid), profilePatch({ street: s(a.street), city: s(a.city), state: s(a.state), zip: s(a.zip) }), { merge: true });
    } catch (e) { setBusy(''); setErr('Your profile could not be saved: ' + ((e && (e.code || e.message)) || 'refused') + '.'); return; }
    setBusy('');
    if (mine) {
      const ok = await write({ claimantStreet: s(a.street), claimantCity: s(a.city), claimantState: s(a.state), claimantZip: s(a.zip) }, 'addr');
      if (ok) setAddr(null);
    } else { setAddr(null); if (onPatched) onPatched({}, { profileChanged: true }); }
  };

  // ---------------------------------------------------------------- RULING 260: declining to sign
  const declined = claim.claimantDeclinedSignature && claim.claimantDeclinedSignature.choice === 'declined' ? claim.claimantDeclinedSignature : null;
  const decline = async () => {
    const ok = await write({ claimantDeclinedSignature: {
      choice: 'declined', acknowledgedAt: new Date().toISOString(), uid, name: s(user && user.name),
      wordingVersion: UNSIGNED_WARNING_VERSION, wording: UNSIGNED_WARNING.join('\n'),
    } }, 'decline');
    if (ok) setWarnOpen(false);
  };
  const willSign = () => write({ claimantDeclinedSignature: {
    choice: 'will-sign', changedAt: new Date().toISOString(), uid, name: s(user && user.name),
    previous: declined ? { acknowledgedAt: declined.acknowledgedAt, wordingVersion: declined.wordingVersion } : null,
  } }, 'sign');

  const box = { border: '1px solid ' + C.line, background: C.panel, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: C.text };
  const btn = (primary) => ({ fontSize: 11, marginRight: 8 });

  return (
    <div>
      {err ? <div style={{ ...box, borderColor: C.bad, color: C.bad }}>&#9888; {err}</div> : null}

      {/* RULING 258 - the Statement of Claim */}
      <div style={box}>
        <div style={{ fontSize: 10, color: approved ? C.ok : C.accent, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>
          Statement of Claim {socWithdrawn ? '- withdrawn by the claimant (kept on the claim, below)' : approved ? '- approved by ' + (s(approved.approvedByName) || 'the claimant') + (approved.approvedAt ? ' on ' + String(approved.approvedAt).slice(0, 10) : '') : (drafted ? '- drafted in the agreement’s words, not yet approved' : '- the claimant’s own words, not yet approved')}
        </div>
        {editing ? (
          <>
            <textarea aria-label="The letter text" className="form-textarea" rows={5} spellCheck value={text} onChange={e => setText(e.target.value)} />
            <ClaimLanguageHelper modes={['statement']} text={text} facts={factsFromClaim(claim)}
              acceptLabel={(mode) => mode === 'proofread' ? 'Use the corrected wording' : 'Use this wording'}
              onUse={(t) => setText(t)} />
          </>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{approved ? approved.text : shown}</div>
        )}
        {mayApprove ? (
          <div style={{ marginTop: 8 }}>
            {!editing && !approved && <button className="btn btn-primary btn-sm" style={btn(true)} disabled={!!busy || !shown || !!actBlocked}
              onClick={() => saveStatement(shown, false)}>
              {busy === 'approve' ? 'Saving…' : 'Approve this statement'}</button>}
            {!editing && <button className="btn btn-secondary btn-sm" style={btn()} disabled={!!busy || !!actBlocked} onClick={() => { setText(approved ? approved.text : shown); setEditing(true); }}>Edit</button>}
            {editing && <button className="btn btn-primary btn-sm" style={btn(true)} disabled={!!busy || !s(text)}
              onClick={async () => { const ok = await saveStatement(s(text), s(text) !== shown); if (ok) setEditing(false); }}>
              {busy === 'approve' ? 'Saving…' : 'Approve my edited statement'}</button>}
            {editing && <button className="btn btn-secondary btn-sm" style={btn()} onClick={() => setEditing(false)}>Cancel</button>}
            <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{mine ? 'What you first wrote stays on your claim exactly as you wrote it.' : 'The claimant has no account, so you approve it for him. His own words stay on the claim exactly as written.'}</div>
            {actBlocked ? <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>{actBlocked}</div> : null}
            {statementByAct && !actBlocked ? <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Your claim is filed, so a change is recorded as a dated act: the earlier text is kept, and your Local and General Chairpersons are told.</div> : null}
          </div>
        ) : (!approved ? <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>The claimant approves the statement from her own claim page.</div> : null)}
      </div>

      {/* Record 605 (ruling 138): the record of changes to her facts, and "Withdraw this statement" */}
      <FactsActsPanel claimId={claimId} claim={claim} user={user} onPatched={onPatched} />

      {/* RULING 259 - the address, asked for with grace */}
      {(needClaimantAddr || needOfficerAddr) && (
        <div style={box}>
          <div style={{ marginBottom: 6 }}>Where should the Carrier send its reply? Enter {mine ? 'your' : 'your office’s'} mailing address once - it is saved to your profile{mine ? ' and to this claim' : ''} and used on every claim you file.</div>
          {!addr ? <button className="btn btn-primary btn-sm" onClick={openAddr}>Add my mailing address</button> : (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 60px 90px', gap: 6 }}>
              {['street', 'city', 'state', 'zip'].map(k => (
                <input aria-label="Address line" key={k} className="form-input" style={{ fontSize: 12 }} placeholder={k === 'zip' ? 'ZIP' : k[0].toUpperCase() + k.slice(1)}
                  value={addr[k] || ''} onChange={e => setAddr(a => ({ ...a, [k]: e.target.value }))} />
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <button className="btn btn-primary btn-sm" style={btn(true)} disabled={busy === 'addr'} onClick={saveAddr}>{busy === 'addr' ? 'Saving…' : 'Save'}</button>
                <button className="btn btn-secondary btn-sm" style={btn()} onClick={() => setAddr(null)}>Not now</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RULING 260 - the claimant's choice to sign, or not */}
      {mine && claim.status !== 'draft' && (
        <div style={{ ...box, borderColor: declined ? C.accent : C.line }}>
          {declined ? (
            <>
              <div style={{ color: C.accent }}>You chose not to sign this claim on {String(declined.acknowledgedAt).slice(0, 10)}. Your Local or General Chairperson may submit it with his signature.</div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} disabled={!!busy} onClick={willSign}>{busy === 'sign' ? 'Saving…' : 'I will sign it after all'}</button>
            </>
          ) : (
            <>
              <div>Your signature is your authority for this claim. It is highly recommended that you sign it.</div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => setWarnOpen(true)}>I do not want to sign - my Local or General Chairperson will submit it</button>
            </>
          )}
        </div>
      )}

      {warnOpen && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#0D1B2A', border: '2px solid ' + C.accent, borderRadius: 8, maxWidth: 520, width: '100%', padding: 20, color: C.text }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 10 }}>{UNSIGNED_WARNING[0]}</div>
            {UNSIGNED_WARNING.slice(1).map((l, i) => <div key={i} style={{ fontSize: 13, marginBottom: 6 }}>{l}</div>)}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => setWarnOpen(false)}>Go back and sign</button>
              <button className="btn btn-secondary" disabled={!!busy} onClick={decline}>{busy === 'decline' ? 'Recording…' : 'I understand. Submit it without my signature'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
